import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { configureApi } from "@/lib/apiClientRuntime";
import { useTranslation } from "react-i18next";
import { initGtmForDemo, trackDemoStart } from "@/gtm.client";

const LOGIN_URL = "https://id.boona.app/login";

function readTokenFromLocation(): string | null {
  // Log full URL for debugging Safari issues
  console.info("[SSO] Current URL:", window.location.href);
  console.info("[SSO] Hash:", window.location.hash);
  console.info("[SSO] Search:", window.location.search);

  // Prefer fragment (#token=...), fallback to ?token=...
  const fromHash = new URLSearchParams(window.location.hash.slice(1)).get("token");
  if (fromHash) {
    console.info("[SSO] Token found in hash fragment");
    return fromHash;
  }

  const fromSearch = new URLSearchParams(window.location.search).get("token");
  if (fromSearch) {
    console.info("[SSO] Token found in query string");
    return fromSearch;
  }

  console.warn("[SSO] No token found in URL");
  return null;
}

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1];
    // Add padding if needed
    const padded = payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, "=");
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (e) {
    console.warn("[SSO] Failed to decode JWT payload", e);
    return null;
  }
}

type SsoStatus = "processing" | "success" | "error";

/**
 * SSO landing page: dashboard redirects to FRONTEND_URL/sso#token=<JWT>
 * This component reads the token, decodes basic user info from the JWT
 * (if present), writes tokens + user into AuthRegistry, and redirects to /
 */
export default function SsoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SsoStatus>("processing");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const token = readTokenFromLocation();

    if (!token) {
      setStatus("error");
      setErrorMessage(t("sso.error.token_not_found", "Authorization token not found in URL. There may have been a redirect error."));
      return;
    }

    const payload = decodeJwtPayload(token);
    if (!payload) {
      setStatus("error");
      setErrorMessage(t("sso.error.token_decode_failed", "Failed to decode the authorization token."));
      return;
    }

    const registry = getAuthRegistry();

    // Build readable name: prefer explicit name, then joined given+family names (if non-empty),
    // otherwise fall back to email, preferred_username, then a default.
    const joinedGivenFamily = [payload?.given_name, payload?.family_name].filter(Boolean).join(" ").trim();
    const nameFromJwt = payload?.name ?? (joinedGivenFamily ? joinedGivenFamily : undefined);
    const finalName = nameFromJwt ?? payload?.email ?? payload?.preferred_username ?? "SSO";

    const user = {
      id: payload?.sub ?? payload?.user_id ?? "sso",
      email: payload?.email ?? payload?.preferred_username ?? "",
      name: finalName,
      roles:
        payload?.roles ??
        payload?.role ??
        (payload?.realm_access && payload.realm_access.roles) ??
        [],
    };

    // The signed `demo` claim (minted by identity_service demo.py) is the ONLY
    // trustworthy demo signal — `?demo=1` in the redirect URL is user-typeable and gone
    // after the first client-side navigation.
    const isDemo = payload?.demo === true || payload?.demo === 1;

    // Extract region from JWT payload and normalize it
    const region = payload?.region as string | undefined;
    const normalizedRegion = region && region !== "default" ? region : null;

    // Configure regional API base URL before storing auth
    if (normalizedRegion) {
      const regionalBase = `https://api-${normalizedRegion}.boona.app`;
      configureApi(regionalBase);
      console.info(`[SSO] Configured API base to ${regionalBase}`);
    }

    // Write authenticated state into registry (includes region)
    try {
      registry.setAuth(user as any, { accessToken: token, refreshToken: null },
                       normalizedRegion, isDemo);
      console.info("[SSO] Auth state saved successfully");

      // Demo sessions only. id.boona.app/demo is a 302 that renders no HTML, so without
      // this the demo step of the paid funnel has no data at all — see src/gtm.client.ts.
      initGtmForDemo(isDemo);
      // `sid` is minted fresh by /demo on every hit, so passing it is what lets GTM tell a
      // first entry from a re-entry — the Back-button history trap looked identical to a
      // normal entry until this was wired. See gtm.client.ts.
      trackDemoStart(normalizedRegion, (payload?.sid as string | undefined) ?? null);

      setStatus("success");

      // "Book" on a dashboard client card (boona clients CL3b) arrives as /sso?book=<id>: the
      // journal opens with that client waiting for a free-time click (InboxBridge)
      const book = new URLSearchParams(window.location.search).get("book");
      // "Open in the journal" from the dashboard's Appointments (boona appointments D7): /sso?open=<id>
      const open = new URLSearchParams(window.location.search).get("open");
      const next = book && /^[0-9]+$/.test(book) ? `/?book=${book}` : open && /^[0-9]+$/.test(open) ? `/?open=${open}` : "/";

      // Small delay to ensure localStorage write completes (Safari compatibility)
      setTimeout(() => {
        navigate(next, { replace: true });
      }, 100);
    } catch (e) {
      console.error("[SSO] Failed to save auth state:", e);
      setStatus("error");
      setErrorMessage(t("sso.error.session_save_failed", "Failed to save session. Please try logging in again."));
    }
  }, [navigate]);

  // Error state - show message and login button
  if (status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center space-y-4 p-8 max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold text-gray-900">{t("sso.error.login_error_title", "Login error")}</h1>
          <p className="text-gray-600">{errorMessage}</p>
          <a
            href={LOGIN_URL}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-block"
          >
            {t("sso.login_again", "Log in again")}
          </a>
        </div>
      </div>
    );
  }

  // Processing/success state - show loading
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="text-center space-y-4">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
        <p className="text-gray-600 text-lg">{t("sso.login_success_loading", "Login successful, loading...")}</p>
      </div>
    </div>
  );
}


